import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import WebsiteIcon from '@mui/icons-material/Language';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewIcon from '@mui/icons-material/GridView';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';

import ErrorNotification from '../../components/ErrorNotification';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import { notifyCreditsChanged } from '../../services/creditsApi';

const brandColor = PRIMARYCOLOR || '#005CC5';

const TYPE_META = {
  vishing: { label: 'Vishing', icon: PhoneIcon, color: '#f59e0b' },
  darkweb: { label: 'Darkweb', icon: LockIcon, color: '#7c3aed' },
  api: { label: 'API', icon: WebsiteIcon, color: '#0ea5e9' },
  web: { label: 'Web', icon: WebsiteIcon, color: '#2563eb' },
  external: { label: 'External', icon: PublicIcon, color: '#16a34a' },
  osint: { label: 'OSINT', icon: TravelExploreIcon, color: '#005CC5' },
};

const typeKeys = ['external', 'web', 'api', 'vishing', 'darkweb', 'osint'];
const statusKeys = ['all', 'running', 'complete', 'failed'];

const normalizePentestType = (value) => {
  const normalized = String(value || 'external').toLowerCase();
  if (normalized.includes('osint')) return 'osint';
  if (normalized.includes('dark')) return 'darkweb';
  if (normalized.includes('vishing')) return 'vishing';
  if (normalized.includes('api')) return 'api';
  if (normalized.includes('web')) return 'web';
  return 'external';
};

const getGrade = (score) => {
  if (score === null || score === undefined) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const getGradeColor = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-':
      return '#16a34a';
    case 'B':
      return '#2563eb';
    case 'C':
      return '#f59e0b';
    case 'D':
      return '#eab308';
    case 'F':
      return '#ef4444';
    default:
      return '#94a3b8';
  }
};

const clamp0to100 = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;

const statusStyle = {
  running: {
    key: 'running',
    label: 'Running',
    color: '#0284c7',
    soft: alpha('#0284c7', 0.1),
    border: alpha('#0284c7', 0.18),
    icon: PlayCircleOutlineIcon,
  },
  complete: {
    key: 'complete',
    label: 'Complete',
    color: '#16a34a',
    soft: alpha('#16a34a', 0.1),
    border: alpha('#16a34a', 0.18),
    icon: CheckCircleOutlineIcon,
  },
  failed: {
    key: 'failed',
    label: 'Failed',
    color: '#dc2626',
    soft: alpha('#dc2626', 0.1),
    border: alpha('#dc2626', 0.18),
    icon: WarningAmberIcon,
  },
};

const normalizeBackendStatus = (status) => String(status || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const explicitStatusInfo = (typeKey, rawStatus) => {
  const normalized = normalizeBackendStatus(rawStatus);
  if (!normalized) return null;

  if (typeKey === 'osint') {
    if (['completed', 'complete', 'done', 'success', 'succeeded'].includes(normalized)) {
      return { ...statusStyle.complete, label: 'Completed' };
    }
    if (['failed', 'fail', 'error', 'errored'].includes(normalized)) {
      return statusStyle.failed;
    }
    return statusStyle.running;
  }

  if (typeKey === 'vishing') {
    if (['done', 'completed', 'complete', 'success', 'succeeded'].includes(normalized)) {
      return { ...statusStyle.complete, label: 'Done' };
    }
    if (['failed', 'fail', 'error', 'errored'].includes(normalized)) {
      return statusStyle.failed;
    }
    return statusStyle.running;
  }

  return null;
};

const getStatusInfo = (score, typeKey, rawStatus) => {
  const explicit = explicitStatusInfo(typeKey, rawStatus);
  if (explicit) return explicit;
  return score === null || score === undefined ? statusStyle.running : statusStyle.complete;
};

const formatDateTime = (value) => {
  if (!value) return 'Unknown date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown date';
  return parsed.toLocaleString();
};

const formatRelativeTime = (value) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';

  const diffMs = parsed.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  const units = [
    ['day', 1000 * 60 * 60 * 24],
    ['hour', 1000 * 60 * 60],
    ['minute', 1000 * 60],
  ];

  for (const [unit, amount] of units) {
    const valueInUnit = Math.round(diffMs / amount);
    if (Math.abs(valueInUnit) >= 1) {
      return rtf.format(valueInUnit, unit);
    }
  }

  return 'just now';
};

const truncate = (value, max = 96) => {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max - 1)}...` : value;
};

const getPentestRoute = (row) => {
  const typeKey = row.typeKey || normalizePentestType(row.type);
  if (typeKey === 'vishing') return `/pentest/vishing/dashboard/${row.id}`;
  if (typeKey === 'darkweb') return `/pentest/darkweb/dashboard/${row.id}`;
  if (typeKey === 'api') return `/pentest/api/dashboard/${row.id}`;
  if (typeKey === 'web') return `/pentest/web/dashboard/${row.id}`;
  if (typeKey === 'osint') return `/pentest/osint/dashboard/${row.id}`;
  return `/pentest/dashboard/${row.id}`;
};

const SummaryCard = ({ title, value, helper, accent, icon }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 4,
      border: `1px solid ${alpha(accent, 0.14)}`,
      background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, rgba(255,255,255,0.98) 58%)`,
      boxShadow: `0 16px 32px ${alpha(accent, 0.08)}`,
    }}
  >
    <CardContent sx={{ p: 2.2 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: alpha('#0f172a', 0.6) }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.8, fontSize: '1.95rem', lineHeight: 1, fontWeight: 900, color: '#0f172a' }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ width: 42, height: 42, bgcolor: alpha(accent, 0.14), color: accent, border: `1px solid ${alpha(accent, 0.18)}` }}>
          {icon}
        </Avatar>
      </Stack>
      <Typography sx={{ mt: 1.25, fontSize: '0.88rem', lineHeight: 1.55, color: alpha('#0f172a', 0.72) }}>
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const StatusChip = ({ status }) => {
  const Icon = status.icon;
  return (
    <Chip
      icon={<Icon fontSize="small" />}
      label={status.label}
      size="small"
      sx={{
        bgcolor: status.soft,
        color: status.color,
        border: `1px solid ${status.border}`,
        fontWeight: 800,
        borderRadius: 999,
        '& .MuiChip-icon': { color: status.color },
      }}
    />
  );
};

const TypeChip = ({ meta }) => {
  const Icon = meta.icon;
  return (
    <Chip
      icon={<Icon fontSize="small" />}
      label={meta.label}
      size="small"
      sx={{
        bgcolor: alpha(meta.color, 0.1),
        color: meta.color,
        border: `1px solid ${alpha(meta.color, 0.16)}`,
        borderRadius: 999,
        fontWeight: 800,
        '& .MuiChip-icon': { color: meta.color },
      }}
    />
  );
};

const GradeDisplay = ({ row }) => {
  if (row.typeKey === 'osint') {
    const neutralColor = '#64748b';
    return (
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress variant="determinate" value={100} size={42} thickness={5} sx={{ color: alpha(neutralColor, 0.28) }} />
          <Box sx={{ inset: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: alpha(neutralColor, 0.14), color: neutralColor, border: `1px solid ${alpha(neutralColor, 0.22)}`, fontWeight: 950 }}>
              N/A
            </Avatar>
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' }}>Not applicable</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: alpha('#0f172a', 0.55) }}>OSINT scan</Typography>
        </Box>
      </Stack>
    );
  }

  if (row.status.key === 'running') {
    return (
      <Chip
        label="Pending"
        size="small"
        sx={{
          bgcolor: alpha(row.status.color, 0.08),
          color: row.status.color,
          border: `1px solid ${row.status.border}`,
          fontWeight: 800,
          borderRadius: 999,
        }}
      />
    );
  }

  if (!row.hasRiskScore) {
    return (
      <Chip
        label="No score"
        size="small"
        sx={{
          bgcolor: alpha('#64748b', 0.08),
          color: '#64748b',
          border: `1px solid ${alpha('#64748b', 0.18)}`,
          fontWeight: 800,
          borderRadius: 999,
        }}
      />
    );
  }

  return (
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress variant="determinate" value={row.riskScore} size={42} thickness={5} sx={{ color: row.gradeColor }} />
        <Box sx={{ inset: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: row.gradeColor, color: '#ffffff', fontWeight: 900 }}>
            {row.grade}
          </Avatar>
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' }}>{row.riskScore} / 100</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: alpha('#0f172a', 0.55) }}>Risk score</Typography>
      </Box>
    </Stack>
  );
};

const PentestPreviewDrawer = ({ open, pentest, onClose, onOpenDashboard, onDelete }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: { xs: '100%', sm: 500 },
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fbff',
      },
    }}
  >
    {pentest ? (
      <>
        <Box
          sx={{
            px: 2.5,
            py: 2.25,
            background: `linear-gradient(135deg, ${brandColor} 0%, ${darken(brandColor, 0.2)} 100%)`,
            color: '#ffffff',
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack spacing={1.25} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <StatusChip status={pentest.status} />
                <TypeChip meta={pentest.typeMeta} />
                {pentest.needsAttention ? (
                  <Chip
                    icon={<WarningAmberIcon fontSize="small" />}
                    label="Needs Attention"
                    size="small"
                    sx={{
                      bgcolor: alpha('#ffffff', 0.12),
                      color: '#ffffff',
                      border: `1px solid ${alpha('#ffffff', 0.22)}`,
                      fontWeight: 800,
                      '& .MuiChip-icon': { color: '#ffffff' },
                    }}
                  />
                ) : null}
              </Stack>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, lineHeight: 1.1 }}>
                {pentest.name}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, color: alpha('#ffffff', 0.92) }}>
                {pentest.summaryText}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: alpha('#ffffff', 0.78) }}>
                Created {pentest.relativeCreated} • {pentest.createdLabel}
              </Typography>
            </Stack>
            <IconButton onClick={onClose} sx={{ color: '#ffffff', alignSelf: 'flex-start' }}>
              <ClearIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: 2.25, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Card elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
                  Targets
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: '1.4rem', fontWeight: 900, color: '#0f172a' }}>
                  {pentest.targetsCount}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
                  Findings
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: '1.4rem', fontWeight: 900, color: '#0f172a' }}>
                  {pentest.findingsCount}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={0} sx={{ p: 1.5, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58) }}>
                  Risk Posture
                </Typography>
                <Box sx={{ mt: 0.85 }}>
                  <GradeDisplay row={pentest} />
                </Box>
              </Card>
            </Grid>
          </Grid>

          <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)', mb: 2 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58), mb: 1 }}>
              Assessment Notes
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.7, color: alpha('#0f172a', 0.78) }}>
              {pentest.details || 'No additional notes were provided for this pentest yet.'}
            </Typography>
          </Card>

          <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(15,23,42,0.08)' }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: alpha('#0f172a', 0.58), mb: 1 }}>
              Signals
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={`Type: ${pentest.typeMeta.label}`} sx={{ borderRadius: 999 }} />
              <Chip label={`Status: ${pentest.status.label}`} sx={{ borderRadius: 999 }} />
              <Chip label={`Created: ${pentest.createdLabel}`} sx={{ borderRadius: 999 }} />
              {pentest.status.key === 'complete' && pentest.typeKey !== 'osint' ? <Chip label={`Grade: ${pentest.grade}`} sx={{ borderRadius: 999 }} /> : null}
            </Stack>
          </Card>
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid rgba(15,23,42,0.08)', bgcolor: '#ffffff' }}>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={onOpenDashboard} sx={{ flex: 1, textTransform: 'none', fontWeight: 800, borderRadius: 2.5 }}>
              Open Dashboard
            </Button>
            <Button variant="outlined" color="error" onClick={onDelete} sx={{ textTransform: 'none', borderRadius: 2.5 }}>
              Delete
            </Button>
          </Stack>
        </Box>
      </>
    ) : null}
  </Drawer>
);

const PentestGridCard = ({ row, onOpen, onDelete }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 4,
      border: `1px solid ${row.needsAttention ? alpha('#ef4444', 0.16) : alpha('#0f172a', 0.08)}`,
      boxShadow: row.needsAttention ? `0 18px 34px ${alpha('#ef4444', 0.08)}` : '0 16px 34px rgba(15,23,42,0.06)',
      background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)',
    }}
  >
    <CardContent sx={{ p: 2, flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.2} sx={{ mb: 1.25 }}>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <StatusChip status={row.status} />
          <TypeChip meta={row.typeMeta} />
        </Stack>
        {row.needsAttention ? (
          <Chip
            icon={<WarningAmberIcon fontSize="small" />}
            label="Needs Attention"
            size="small"
            sx={{
              bgcolor: alpha('#ef4444', 0.08),
              color: '#ef4444',
              border: `1px solid ${alpha('#ef4444', 0.14)}`,
              fontWeight: 800,
              '& .MuiChip-icon': { color: '#ef4444' },
            }}
          />
        ) : null}
      </Stack>

      <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color: '#0f172a', mb: 0.7 }}>
        {row.name}
      </Typography>
      <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.6, color: alpha('#0f172a', 0.72), mb: 1.25 }}>
        {row.summaryText}
      </Typography>

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccessTimeIcon sx={{ fontSize: 16, color: alpha('#0f172a', 0.55) }} />
          <Typography sx={{ fontSize: '0.8rem', color: alpha('#0f172a', 0.72) }}>
            {row.relativeCreated} • {row.createdLabel}
          </Typography>
        </Stack>
        <GradeDisplay row={row} />
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip label={`${row.targetsCount} targets`} sx={{ borderRadius: 999 }} />
          <Chip label={`${row.findingsCount} findings`} sx={{ borderRadius: 999 }} />
        </Stack>
      </Stack>
    </CardContent>

    <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
      <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={() => onOpen(row)} sx={{ flex: 1, textTransform: 'none', fontWeight: 800, borderRadius: 2.5 }}>
        Open
      </Button>
      <Tooltip title="Delete pentest" arrow>
        <IconButton color="error" onClick={() => onDelete(row)}>
          <DeleteOutlineIcon />
        </IconButton>
      </Tooltip>
    </CardActions>
  </Card>
);

const Pentests = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [pentestsData, setPentestsData] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [selectedPentest, setSelectedPentest] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPentests = useCallback((token) => {
    setLoading(true);
    fetch(`${BASEURL}/pentests`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          if (data.message === 'Invalid Token') navigate('/');
          else setError(data.message);
          setPentestsData([]);
        } else {
          setPentestsData(data.pentests || []);
          setError(null);
        }
      })
      .catch(() => setError('Error fetching pentests'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) fetchPentests(token);
    else navigate('/');
  }, [fetchPentests, navigate]);

  const pentests = useMemo(
    () =>
      (pentestsData || []).map((row) => {
        const typeKey = normalizePentestType(row.type);
        const typeMeta = TYPE_META[typeKey] || TYPE_META.external;
        const status = getStatusInfo(row.risk_score, typeKey, row.status);
        const hasRiskScore = row.risk_score !== null && row.risk_score !== undefined && Number.isFinite(Number(row.risk_score));
        const riskScore = clamp0to100(Number(row.risk_score));
        const grade = hasRiskScore ? getGrade(Number(row.risk_score)) : '?';
        const gradeColor = getGradeColor(grade);
        const findingsCount = Number(row.exploits_count || 0);
        const targetsCount = Number(row.assets_count || 0);
        const needsAttention = status.key === 'failed' || (typeKey !== 'osint' && status.key === 'complete' && hasRiskScore && riskScore < 80);

        return {
          ...row,
          typeKey,
          typeMeta,
          status,
          hasRiskScore,
          riskScore,
          grade,
          gradeColor,
          findingsCount,
          targetsCount,
          needsAttention,
          relativeCreated: formatRelativeTime(row.created_at),
          createdLabel: formatDateTime(row.created_at),
          summaryText: truncate(row.details || `${targetsCount} targets assessed with ${findingsCount} findings identified.`, 110),
        };
      }),
    [pentestsData]
  );

  const summary = useMemo(() => {
    const running = pentests.filter((item) => item.status.key === 'running').length;
    const complete = pentests.filter((item) => item.status.key === 'complete').length;
    const attention = pentests.filter((item) => item.needsAttention).length;
    const failed = pentests.filter((item) => item.status.key === 'failed').length;
    const findings = pentests.reduce((total, item) => total + item.findingsCount, 0);
    const typeCounts = typeKeys.map((key) => ({
      key,
      meta: TYPE_META[key],
      count: pentests.filter((item) => item.typeKey === key).length,
    }));

    return { running, complete, attention, failed, findings, typeCounts };
  }, [pentests]);

  const filteredPentests = useMemo(() => {
    const normalized = searchInput.trim().toLowerCase();

    return pentests.filter((item) => {
      const matchesSearch =
        !normalized ||
        [item.name, item.typeMeta.label, item.details, item.status.label]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(normalized);

      const matchesType = !selectedTypes.size || selectedTypes.has(item.typeKey);
      const matchesStatus = statusFilter === 'all' || item.status.key === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [pentests, searchInput, selectedTypes, statusFilter]);

  const sortedPentests = useMemo(() => {
    const items = [...filteredPentests];
    items.sort((a, b) => {
      const getValue = (item) => {
        switch (sortBy) {
          case 'status':
            return { failed: 0, running: 1, complete: 2 }[item.status.key] ?? 3;
          case 'risk_score':
            return item.status.key === 'running' || item.status.key === 'failed' ? -1 : item.riskScore;
          case 'assets_count':
            return item.targetsCount;
          case 'exploits_count':
            return item.findingsCount;
          case 'created_at':
            return new Date(item.created_at).getTime();
          default:
            return item[sortBy];
        }
      };

      const av = getValue(a);
      const bv = getValue(b);

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === 'string' && typeof bv === 'string') {
        return sortOrder === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }

      if (av < bv) return sortOrder === 'asc' ? -1 : 1;
      if (av > bv) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return items;
  }, [filteredPentests, sortBy, sortOrder]);

  const paginatedPentests = useMemo(
    () => sortedPentests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedPentests, page, rowsPerPage]
  );

  const toggleType = (key) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedPentest(null);
  };

  const handleDeletePentest = (pentest) => {
    setSelectedPentest(pentest);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    const token = sessionStorage.getItem('token');
    fetch(`${BASEURL}/pentests?pentest_id=${selectedPentest.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          notifyCreditsChanged({ reason: 'pentest-deleted', pentestId: selectedPentest.id });
          setPentestsData((prev) => prev.filter((item) => item.id !== selectedPentest.id));
          setError(null);
        } else {
          setError(data.message || 'Failed to delete pentest');
        }
      })
      .catch(() => setError('Failed to delete pentest'))
      .finally(() => {
        setOpenDeleteDialog(false);
        setSelectedPentest(null);
        setOpenPreview(false);
      });
  };

  const openDashboard = (row) => navigate(getPentestRoute(row));
  const showNoPentestMessage = !loading && filteredPentests.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: alpha('#eff6ff', 0.72),
        backgroundImage: `radial-gradient(${alpha('#bfdbfe', 0.55)} 1px, transparent 1px)`,
        backgroundSize: '18px 18px',
      }}
    >
      <ErrorNotification errorMessage={error} />
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 5,
            border: `1px solid ${alpha(brandColor, 0.14)}`,
            bgcolor: '#ffffff',
            boxShadow: `0 18px 42px ${alpha(brandColor, 0.08)}`,
            mb: 2.4,
          }}
        >
          <Box aria-hidden sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: brandColor }} />
          <Box aria-hidden sx={{ position: 'absolute', top: -90, right: -30, width: 260, height: 260, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(brandColor, 0.14)} 0%, rgba(255,255,255,0) 72%)` }} />
          <Box sx={{ position: 'relative', p: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2.5}>
              <Box sx={{ maxWidth: 760 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: brandColor }}>
                  Pentests
                </Typography>
                <Typography variant="h3" sx={{ mt: 0.8, fontWeight: 900, letterSpacing: '-0.03em', color: '#0f172a', fontSize: { xs: '2rem', md: '2.45rem' } }}>
                  Assessment Workspace
                </Typography>
                <Typography sx={{ mt: 1, maxWidth: 560, fontSize: '0.98rem', lineHeight: 1.6, color: alpha('#0f172a', 0.68) }}>
                  Monitor running scans, review completed reports, and jump into the right dashboard.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.6 }}>
                  {summary.typeCounts.map((item) => {
                    const Icon = item.meta.icon;
                    return (
                      <Chip
                        key={item.key}
                        icon={<Icon fontSize="small" />}
                        label={`${item.meta.label}: ${item.count}`}
                        sx={{
                          bgcolor: alpha(item.meta.color, 0.08),
                          color: item.meta.color,
                          border: `1px solid ${alpha(item.meta.color, 0.14)}`,
                          fontWeight: 800,
                          '& .MuiChip-icon': { color: item.meta.color },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
              <Stack spacing={1.2} alignItems={{ xs: 'stretch', lg: 'flex-end' }}>
                <Button
                  onClick={() => navigate('/pentests/create')}
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    borderRadius: 2.5,
                    px: 2.4,
                    py: 1.2,
                  }}
                >
                  New Pentest
                </Button>
                <Typography sx={{ fontSize: '0.82rem', color: alpha('#0f172a', 0.58) }}>
                  {filteredPentests.length} shown out of {pentests.length} total assessments
                </Typography>
              </Stack>
            </Stack>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard title="Total Pentests" value={pentests.length} helper="Every active and completed assessment currently in the workspace." accent={brandColor} icon={<SecurityIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard title="Running" value={summary.running} helper="Assessments still being processed or waiting on agent completion." accent="#0284c7" icon={<PlayCircleOutlineIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard title="Complete" value={summary.complete} helper="Assessments marked complete, done, or report-ready." accent="#16a34a" icon={<CheckCircleOutlineIcon />} />
              </Grid>
              <Grid item xs={12} sm={6} xl={3}>
                <SummaryCard title="Needs Attention" value={summary.attention} helper="Failed scans or completed assessments scoring below 80." accent="#ef4444" icon={<WarningAmberIcon />} />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.6, md: 2 },
            borderRadius: 4,
            border: `1px solid ${alpha('#0f172a', 0.08)}`,
            bgcolor: '#ffffff',
            boxShadow: '0 14px 28px rgba(15,23,42,0.04)',
            mb: 2.4,
          }}
        >
          <Stack spacing={1.75}>
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.5} justifyContent="space-between">
              <TextField
                fullWidth
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  setPage(0);
                }}
                placeholder="Search by pentest name, type, status, or notes..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchInput ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput('');
                        setPage(0);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  ) : null,
                }}
                sx={{ maxWidth: { xl: 520 }, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" sx={{ flex: 1 }}>
                <ToggleButtonGroup
                  value={statusFilter}
                  exclusive
                  size="small"
                  onChange={(_, value) => {
                    if (value) {
                      setStatusFilter(value);
                      setPage(0);
                    }
                  }}
                  sx={{ '& .MuiToggleButton-root': { borderRadius: 2.5, px: 1.4, textTransform: 'none', fontWeight: 700 } }}
                >
                  {statusKeys.map((key) => (
                    <ToggleButton key={key} value={key}>
                      {key === 'all' ? 'All statuses' : key === 'running' ? 'Running' : key === 'failed' ? 'Failed' : 'Complete'}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  size="small"
                  onChange={(_, value) => value && setViewMode(value)}
                  sx={{ '& .MuiToggleButton-root': { borderRadius: 2.5, px: 1.4, textTransform: 'none', fontWeight: 700 } }}
                >
                  <ToggleButton value="table">
                    <TableRowsIcon sx={{ mr: 0.75, fontSize: 18 }} />
                    Table
                  </ToggleButton>
                  <ToggleButton value="grid">
                    <GridViewIcon sx={{ mr: 0.75, fontSize: 18 }} />
                    Cards
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} justifyContent="space-between">
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {typeKeys.map((key) => {
                  const meta = TYPE_META[key];
                  const Icon = meta.icon;
                  const selected = selectedTypes.has(key);
                  return (
                    <Chip
                      key={key}
                      icon={<Icon fontSize="small" />}
                      label={meta.label}
                      clickable
                      onClick={() => toggleType(key)}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        bgcolor: selected ? alpha(meta.color, 0.12) : alpha('#0f172a', 0.04),
                        color: selected ? meta.color : alpha('#0f172a', 0.68),
                        border: `1px solid ${selected ? alpha(meta.color, 0.16) : alpha('#0f172a', 0.08)}`,
                        '& .MuiChip-icon': { color: selected ? meta.color : alpha('#0f172a', 0.58) },
                      }}
                    />
                  );
                })}
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {[
                  { label: 'Newest', sortBy: 'created_at', order: 'desc' },
                  { label: 'Highest Risk', sortBy: 'risk_score', order: 'asc' },
                  { label: 'Most Findings', sortBy: 'exploits_count', order: 'desc' },
                ].map((preset) => {
                  const selected = sortBy === preset.sortBy && sortOrder === preset.order;
                  return (
                    <Chip
                      key={preset.label}
                      icon={<AnalyticsOutlinedIcon fontSize="small" />}
                      label={preset.label}
                      clickable
                      onClick={() => {
                        setSortBy(preset.sortBy);
                        setSortOrder(preset.order);
                      }}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        bgcolor: selected ? alpha(brandColor, 0.12) : alpha('#0f172a', 0.04),
                        color: selected ? brandColor : alpha('#0f172a', 0.68),
                        border: `1px solid ${selected ? alpha(brandColor, 0.16) : alpha('#0f172a', 0.08)}`,
                        '& .MuiChip-icon': { color: selected ? brandColor : alpha('#0f172a', 0.58) },
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        <Card sx={{ p: 0, borderRadius: 4, overflow: 'hidden', border: `1px solid ${alpha('#0f172a', 0.08)}`, boxShadow: '0 16px 34px rgba(15,23,42,0.05)' }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              {[...Array(6)].map((_, index) => (
                <Skeleton key={index} variant="rounded" height={64} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : showNoPentestMessage ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 2,
                border: (theme) => `2px dashed ${alpha(theme.palette.divider, 0.8)}`,
                borderRadius: 3,
                m: 3,
                bgcolor: alpha('#ffffff', 0.7),
              }}
            >
              <SecurityIcon sx={{ fontSize: 56, color: brandColor, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                No Pentests Match Those Filters
              </Typography>
              <Typography sx={{ mb: 2, color: 'text.secondary' }}>
                Try clearing your filters or launch a new assessment to get started.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/pentests/create')} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700 }}>
                Create a Pentest
              </Button>
            </Box>
          ) : viewMode === 'grid' ? (
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                {paginatedPentests.map((row) => (
                  <Grid item xs={12} md={6} xl={4} key={row.id || row.name}>
                    <PentestGridCard
                      row={row}
                      onOpen={openDashboard}
                      onDelete={handleDeletePentest}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          background: `linear-gradient(180deg, ${alpha(brandColor, 0.94)}, ${alpha(darken(brandColor, 0.08), 0.94)})`,
                          color: '#ffffff',
                          fontWeight: 800,
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <TableCell sortDirection={sortBy === 'name' ? sortOrder : false}>
                        <TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortOrder : 'asc'} onClick={() => handleSort('name')} sx={{ color: '#ffffff', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}>
                          Pentest
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={sortBy === 'status' ? sortOrder : false}>
                        <TableSortLabel active={sortBy === 'status'} direction={sortBy === 'status' ? sortOrder : 'asc'} onClick={() => handleSort('status')} sx={{ color: '#ffffff', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}>
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell width={135}>Grade</TableCell>
                      <TableCell sortDirection={sortBy === 'type' ? sortOrder : false}>
                        <TableSortLabel active={sortBy === 'type'} direction={sortBy === 'type' ? sortOrder : 'asc'} onClick={() => handleSort('type')} sx={{ color: '#ffffff', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}>
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={sortBy === 'created_at' ? sortOrder : false}>
                        <TableSortLabel active={sortBy === 'created_at'} direction={sortBy === 'created_at' ? sortOrder : 'asc'} onClick={() => handleSort('created_at')} sx={{ color: '#ffffff', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}>
                          Created
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={sortBy === 'assets_count' ? sortOrder : false}>
                        <TableSortLabel active={sortBy === 'assets_count'} direction={sortBy === 'assets_count' ? sortOrder : 'asc'} onClick={() => handleSort('assets_count')} sx={{ color: '#ffffff', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}>
                          Targets
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={sortBy === 'exploits_count' ? sortOrder : false}>
                        <TableSortLabel active={sortBy === 'exploits_count'} direction={sortBy === 'exploits_count' ? sortOrder : 'asc'} onClick={() => handleSort('exploits_count')} sx={{ color: '#ffffff', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}>
                          Findings
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" width={120}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody
                    sx={{
                      '& tr:nth-of-type(odd)': { backgroundColor: alpha('#f8fafc', 0.9) },
                      '& tr:hover': { backgroundColor: alpha(brandColor, 0.05) },
                    }}
                  >
                    {paginatedPentests.map((row) => (
                      <TableRow
                        key={row.id || row.name}
                        hover
                        onClick={() => openDashboard(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ minWidth: 300 }}>
                          <Stack spacing={0.7}>
                            <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{row.name}</Typography>
                            <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.55, color: alpha('#0f172a', 0.65) }}>
                              {row.summaryText}
                            </Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              {row.needsAttention ? (
                                <Chip
                                  icon={<WarningAmberIcon fontSize="small" />}
                                  label="Needs Attention"
                                  size="small"
                                  sx={{
                                    bgcolor: alpha('#ef4444', 0.08),
                                    color: '#ef4444',
                                    border: `1px solid ${alpha('#ef4444', 0.14)}`,
                                    '& .MuiChip-icon': { color: '#ef4444' },
                                  }}
                                />
                              ) : row.status.key === 'failed' ? (
                                <Chip
                                  icon={<WarningAmberIcon fontSize="small" />}
                                  label="Scan failed"
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(row.status.color, 0.08),
                                    color: row.status.color,
                                    border: `1px solid ${row.status.border}`,
                                    '& .MuiChip-icon': { color: row.status.color },
                                  }}
                                />
                              ) : row.status.key === 'running' ? (
                                <Chip
                                  icon={<AccessTimeIcon fontSize="small" />}
                                  label={row.typeKey === 'osint' || row.typeKey === 'vishing' ? 'Awaiting completion' : 'Awaiting score'}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(row.status.color, 0.08),
                                    color: row.status.color,
                                    border: `1px solid ${row.status.border}`,
                                    '& .MuiChip-icon': { color: row.status.color },
                                  }}
                                />
                              ) : null}
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell><StatusChip status={row.status} /></TableCell>
                        <TableCell><GradeDisplay row={row} /></TableCell>
                        <TableCell><TypeChip meta={row.typeMeta} /></TableCell>
                        <TableCell>
                          <Tooltip title={row.createdLabel} arrow>
                            <Stack spacing={0.4}>
                              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{row.relativeCreated}</Typography>
                              <Typography sx={{ fontSize: '0.74rem', color: alpha('#0f172a', 0.55) }}>{row.createdLabel}</Typography>
                            </Stack>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={row.targetsCount} size="small" sx={{ fontWeight: 800, borderRadius: 999, bgcolor: alpha('#16a34a', 0.08), color: '#15803d' }} />
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={row.findingsCount} size="small" sx={{ fontWeight: 800, borderRadius: 999, bgcolor: alpha('#ef4444', 0.08), color: '#dc2626' }} />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.75} justifyContent="center">
                            <Tooltip title="Open dashboard" arrow>
                              <IconButton
                                color="primary"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openDashboard(row);
                                }}
                              >
                                <OpenInNewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete pentest" arrow>
                              <IconButton
                                color="error"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDeletePentest(row);
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={sortedPentests.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{ px: 2, '& .MuiTablePagination-toolbar': { py: 1.2 } }}
              />
            </>
          )}
        </Card>
      </Box>

      <PentestPreviewDrawer
        open={openPreview}
        pentest={selectedPentest}
        onClose={handleClosePreview}
        onOpenDashboard={() => {
          if (selectedPentest) {
            handleClosePreview();
            openDashboard(selectedPentest);
          }
        }}
        onDelete={() => {
          setOpenDeleteDialog(true);
        }}
      />

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete <strong>{selectedPentest?.name ?? 'this pentest'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pentests;
